import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Sparkles, ExternalLink, Calendar, Calculator, CheckCircle, Clock } from 'lucide-react';
import { PremiumJobboardsData } from '../types';

export default function PremiumChannelsDashboard() {
    const [vacancies, setVacancies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchVacancies();
    }, []);

    const fetchVacancies = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('vacancies')
                .select(`id, title, client_id, data, clients!vacancies_client_id_fkey (name)`)
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Filter out only those with requested premium channels
            const requested = (data || []).filter(v => {
                const jobboards: PremiumJobboardsData = v.data?.premiumJobboards || {};
                return jobboards.linkedin?.selected || jobboards.indeed?.selected || jobboards.werkzoeken?.selected || jobboards.ictergezocht?.selected;
            });

            setVacancies(requested);
        } catch (err) {
            console.error("Failed to fetch premium channels", err);
        } finally {
            setLoading(false);
        }
    };

    const updateChannel = async (vacancyId: string, channel: keyof PremiumJobboardsData, field: string, value: any) => {
        try {
            const vIndex = vacancies.findIndex(v => v.id === vacancyId);
            if (vIndex === -1) return;

            const updatedVacancy = JSON.parse(JSON.stringify(vacancies[vIndex]));
            
            if (!updatedVacancy.data) updatedVacancy.data = {};
            if (!updatedVacancy.data.premiumJobboards) updatedVacancy.data.premiumJobboards = {};
            if (!updatedVacancy.data.premiumJobboards[channel]) updatedVacancy.data.premiumJobboards[channel] = { selected: true };

            updatedVacancy.data.premiumJobboards[channel][field] = value;

            // Optimistic UI Update
            const newVacancies = [...vacancies];
            newVacancies[vIndex] = updatedVacancy;
            setVacancies(newVacancies);

            // Supabase Update
            await supabase.from('vacancies').update({
                data: updatedVacancy.data
            }).eq('id', vacancyId);

        } catch (err) {
            console.error("Failed to update channel", err);
            await fetchVacancies(); // Revert on err
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">Laden van premium aanvragen...</div>;

    if (vacancies.length === 0) return (
        <div className="p-12 text-center flex flex-col items-center">
            <Sparkles size={48} className="text-slate-200 mb-4" />
            <h3 className="text-lg font-black text-slate-900 tracking-tight">Geen Premium Aanvragen</h3>
            <p className="text-sm text-slate-500 font-medium mt-2">Wanneer bedrijven Premium kanalen aanvragen bij een vacature verschijnen ze hier in het overzicht.</p>
        </div>
    );

    return (
        <div className="bg-white rounded-[2.5rem] p-8 border border-red-100 shadow-xl shadow-red-500/5">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 shadow-lg shadow-red-500/20 flex items-center justify-center text-white">
                    <Sparkles size={24} />
                </div>
                <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Premium Kanalen Hub</h2>
                    <p className="text-sm text-slate-500 font-medium">Beheer campagne aanvragen, plaats links en registreer facturaties.</p>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                    <thead>
                        <tr className="uppercase text-[10px] font-black text-slate-400 tracking-widest border-b-2 border-slate-100">
                            <th className="pb-4 pl-4 min-w-[200px]">Vacature & Bedrijf</th>
                            <th className="pb-4">Kanaal</th>
                            <th className="pb-4">Status & Actieve Link</th>
                            <th className="pb-4">Facturatie</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {vacancies.map((v) => {
                            const jobboards: PremiumJobboardsData = v.data?.premiumJobboards || {};
                            const channels = (Object.keys(jobboards) as Array<keyof PremiumJobboardsData>).filter(k => jobboards[k]?.selected);
                            
                            return channels.map((channel, idx) => {
                                const cData = jobboards[channel]!;
                                return (
                                <tr key={`${v.id}-${channel}`} className="hover:bg-slate-50 transition-colors group">
                                    <td className="py-4 pl-4 pr-6 align-top">
                                        <div className="font-bold text-slate-900">{v.title}</div>
                                        <div className="text-xs text-slate-500 mt-1">{v.clients?.name || 'Onbekend bedrijf'}</div>
                                    </td>
                                    <td className="py-4 pr-6 align-top">
                                        <div className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-black capitalize tracking-wide">
                                            {channel}
                                        </div>
                                    </td>
                                    <td className="py-4 pr-6 align-top">
                                        <div className="flex flex-col gap-2">
                                            <select 
                                                className={`text-xs font-bold rounded-lg border px-3 py-2 outline-none transition-all ${cData.status === 'Online' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : cData.status === 'Afgerond' ? 'border-slate-200 bg-slate-100 text-slate-500' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                                                value={cData.status || 'Aangevraagd'}
                                                onChange={(e) => updateChannel(v.id, channel, 'status', e.target.value)}
                                            >
                                                <option value="Aangevraagd">Aangevraagd</option>
                                                <option value="Online">Live (Online)</option>
                                                <option value="Afgerond">Afgerond</option>
                                            </select>

                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <ExternalLink size={12} className="text-slate-400" />
                                                </div>
                                                <input 
                                                    type="url"
                                                    placeholder="Plaats de live URL hier..."
                                                    className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg hover:border-blue-400 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300"
                                                    value={cData.activeLink || ''}
                                                    onChange={(e) => updateChannel(v.id, channel, 'activeLink', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-4 pr-4 align-top">
                                        <div className="flex flex-col gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100/50">
                                            <div className="flex items-center gap-2">
                                                <Calculator size={14} className="text-slate-400" />
                                                <input 
                                                    type="text"
                                                    placeholder="Factuurnr."
                                                    className="w-full text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 hover:border-slate-300 outline-none focus:border-red-400 font-mono text-slate-700"
                                                    value={cData.invoiceNumber || ''}
                                                    onChange={(e) => updateChannel(v.id, channel, 'invoiceNumber', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Calendar size={14} className="text-slate-400" />
                                                <input 
                                                    type="date"
                                                    className="w-full text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 hover:border-slate-300 outline-none focus:border-red-400 text-slate-600"
                                                    value={cData.invoiceDate || ''}
                                                    onChange={(e) => updateChannel(v.id, channel, 'invoiceDate', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                )
                            })
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
